import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import { FiChevronLeft, FiChevronRight, FiClock } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { PopUpSelect } from "./ds/PopUpButton";

const WEEK_DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Visual date + hour/minute/second picker (local wall time in `timezone`).
 * `value` is ISO string in UTC or null.
 */
export default function ConsultingDateTimePicker({
  isDark,
  timezone = "Asia/Tbilisi",
  value,
  onChange,
  minDate,
}) {
  const { t } = useTranslation();
  const tz = timezone || "Asia/Tbilisi";

  const dt = value ? DateTime.fromISO(value, { zone: "utc" }).setZone(tz) : null;
  const initialMonth = dt?.isValid ? dt.toFormat("yyyy-MM") : DateTime.now().setZone(tz).toFormat("yyyy-MM");
  const [viewMonth, setViewMonth] = useState(initialMonth);

  const selectedDay = dt?.isValid ? dt.toFormat("yyyy-MM-dd") : DateTime.now().setZone(tz).toFormat("yyyy-MM-dd");
  const hour = dt?.isValid ? dt.hour : 10;
  const minute = dt?.isValid ? dt.minute : 0;
  const second = dt?.isValid ? dt.second : 0;

  const baseText = "text-label-primary";
  const muted = "text-label-secondary";
  const glass = "border-separator-default bg-bg-raised";
  const input = "border-separator-default bg-bg-inset text-label-primary";

  const monthDt = DateTime.fromFormat(`${viewMonth}-01`, "yyyy-MM-dd", { zone: tz });

  const emit = (dayIso, h, m, s) => {
    const local = DateTime.fromISO(dayIso, { zone: tz }).set({
      hour: h,
      minute: m,
      second: s,
      millisecond: 0,
    });
    if (!local.isValid) return;
    const min = minDate ? DateTime.fromJSDate(minDate).setZone(tz) : DateTime.now().setZone(tz);
    if (local < min) return;
    onChange?.(local.toUTC().toISO());
  };

  const calendarCells = useMemo(() => {
    if (!monthDt.isValid) return [];
    const start = monthDt.startOf("month");
    const end = monthDt.endOf("month");
    const padStart = start.weekday === 7 ? 0 : start.weekday;
    const cells = [];
    for (let i = 0; i < padStart; i += 1) cells.push({ empty: true, key: `p-${i}` });
    for (let d = start; d <= end; d = d.plus({ days: 1 })) {
      const iso = d.toFormat("yyyy-MM-dd");
      cells.push({
        empty: false,
        key: iso,
        iso,
        day: d.day,
        isToday: iso === DateTime.now().setZone(tz).toFormat("yyyy-MM-dd"),
        selected: iso === selectedDay,
      });
    }
    return cells;
  }, [monthDt, selectedDay, tz]);

  const preview = dt?.isValid
    ? dt.toFormat("cccc, LLL d, yyyy · HH:mm:ss")
    : DateTime.fromISO(selectedDay, { zone: tz })
        .set({ hour, minute, second })
        .toFormat("cccc, LLL d, yyyy · HH:mm:ss");

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);
  const seconds = Array.from({ length: 60 }, (_, i) => i);

  return (
    <div className={`overflow-hidden rounded-radius-md border ${glass}`}>
      <div className={`flex items-center justify-between border-b px-3 py-2 border-separator-default`}>
        <button
          type="button"
          onClick={() => setViewMonth(monthDt.minus({ months: 1 }).toFormat("yyyy-MM"))}
          className={`rounded-radius-xs p-1.5 hover:bg-fill-quaternary`}
        >
          <FiChevronLeft className="h-4 w-4" />
        </button>
        <span className={`text-subheadline-semibold ${baseText}`}>
          {monthDt.isValid ? monthDt.toFormat("LLLL yyyy") : viewMonth}
        </span>
        <button
          type="button"
          onClick={() => setViewMonth(monthDt.plus({ months: 1 }).toFormat("yyyy-MM"))}
          className={`rounded-radius-xs p-1.5 hover:bg-fill-quaternary`}
        >
          <FiChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="p-3">
        <div className={`mb-2 grid grid-cols-7 gap-0.5 text-center text-caption-1-semibold uppercase ${muted}`}>
          {WEEK_DAYS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {calendarCells.map((cell) =>
            cell.empty ? (
              <div key={cell.key} className="aspect-square" />
            ) : (
              <button
                key={cell.key}
                type="button"
                onClick={() => emit(cell.iso, hour, minute, second)}
                className={`flex aspect-square items-center justify-center rounded-radius-xs text-footnote-semibold transition ${
                  cell.selected
                    ? "bg-app-consulting-base text-app-consulting-on shadow-md"
                    : cell.isToday
                      ? isDark
                        ? "ring-1 ring-accent-default text-slate-200"
                        : "ring-1 ring-accent-default"
                      : "text-label-secondary hover:bg-fill-quaternary"
                }`}
              >
                {cell.day}
              </button>
            )
          )}
        </div>
      </div>

      <div className={`border-t px-3 py-3 border-separator-default`}>
        <p className={`mb-2 flex items-center gap-1.5 text-caption-1-semibold ${baseText}`}>
          <FiClock className="h-3.5 w-3.5 text-app-consulting-soft" />
          {t("consulting.pickTimeHms") || "Time (hour · minute · second)"}
        </p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: t("consulting.hour") || "Hour", val: hour, opts: hours, field: "h" },
            { label: t("consulting.minute") || "Min", val: minute, opts: minutes, field: "m" },
            { label: t("consulting.second") || "Sec", val: second, opts: seconds, field: "s" },
          ].map(({ label, val, opts, field }) => (
            <div key={field}>
              <span className={`text-caption-1 font-medium ${muted}`}>{label}</span>
              <PopUpSelect
                label={label}
                value={val}
                onChange={(value) => {
                  const n = Number(value);
                  if (field === "h") emit(selectedDay, n, minute, second);
                  if (field === "m") emit(selectedDay, hour, n, second);
                  if (field === "s") emit(selectedDay, hour, minute, n);
                }}
                className="mt-0.5 w-full"
              >
                {opts.map((o) => (
                  <option key={o} value={o}>
                    {pad2(o)}
                  </option>
                ))}
              </PopUpSelect>
            </div>
          ))}
        </div>
        <p className={`mt-3 rounded-radius-sm px-3 py-2 text-center text-caption-1 font-medium ${isDark ? "bg-app-consulting-deep text-app-consulting-soft" : "bg-app-consulting-deep text-app-consulting-soft"}`}>
          {preview}
          <span className={`block text-caption-1 font-normal ${muted}`}>{tz.replace("_", " ")}</span>
        </p>
      </div>
    </div>
  );
}
